<template>
    <Container>
        <div class="my-4 flex w-full items-center justify-between">
            <h1 class="text-2xl font-bold text-white">Songs</h1>
            <div class="flex gap-3">
                <Link href="/backstage/songs/create"
                    class="rounded-md bg-nomad-accent px-4 py-2 font-semibold text-nomad-900 transition-colors hover:bg-nomad-accent/80">
                    Create Song
                </Link>
                <Link href="/backstage"
                    class="rounded-md bg-nomad-900 px-4 py-2 text-white transition-colors hover:bg-nomad-800">
                    Back
                </Link>
            </div>
        </div>

        <div class="mb-4 w-full">
            <input type="text" placeholder="Search songs"
                class="w-full max-w-lg rounded-md border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent" />
        </div>

        <ul v-if="songs?.data?.length" class="grid gap-3">
            <li v-for="song in songs.data" :key="song.id"
                class="flex flex-col gap-3 rounded-xl bg-nomad-900 p-3 text-white transition-colors hover:bg-nomad-800 sm:flex-row sm:items-center sm:gap-4">
                <Link :href="`/backstage/songs/${song.id}/edit`" class="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                    <img :src="song.cover || song.artist?.image || fallbackImage" :alt="`${song.name} cover`"
                        class="h-14 w-14 shrink-0 rounded-lg object-cover sm:h-16 sm:w-16" />
                    <div class="min-w-0 flex-1">
                        <h2 class="truncate text-base font-semibold text-nomad-200 sm:text-lg">{{ song.name }}</h2>
                        <span class="block truncate text-sm text-nomad-300">
                            {{ song.artist?.name || 'Unknown artist' }}
                        </span>
                    </div>
                </Link>

                <div class="flex w-full items-center justify-between gap-3 border-t border-nomad-800 pt-2 sm:w-auto sm:justify-end sm:border-0 sm:pt-0">
                    <div class="flex min-w-0 items-center gap-2 text-sm text-nomad-300 sm:max-w-48">
                        <template v-if="song.meta?.status === 'fail'">
                            <span class="icon-x shrink-0 text-nomad-red" aria-label="Audio processing failed"></span>
                            <button type="button" @click="retryDownload(song.id)"
                                class="rounded-full bg-nomad-700 p-2 text-nomad-accent transition-colors hover:bg-nomad-600"
                                aria-label="Retry audio processing">
                                <span class="icon-repeat" aria-hidden="true"></span>
                            </button>
                        </template>
                        <span v-else-if="song.meta?.status === 'repeat'" class="icon-check shrink-0 text-nomad-accent"
                            aria-label="Audio processed"></span>
                        <span v-else-if="song.meta?.retry" class="icon-hard-drive shrink-0 text-nomad-accent"
                            aria-label="Audio retry in progress"></span>
                        <span v-else-if="song.mp3" class="icon-check shrink-0 text-nomad-blue"
                            aria-label="Audio available"></span>
                        <span v-else class="icon-git-pull-request shrink-0 text-nomad-red"
                            aria-label="Audio not processed"></span>
                        <p v-if="song.meta?.message" class="truncate">{{ song.meta.message }}</p>
                    </div>

                    <button type="button" @click="deleteSong(song.id)" :disabled="deletingSongId === song.id"
                        class="shrink-0 rounded-md p-2 text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                        :aria-label="`Delete ${song.name}`">
                        <span class="icon-x" aria-hidden="true"></span>
                    </button>
                </div>
            </li>
        </ul>
        <p v-else class="rounded-xl bg-nomad-900 p-6 text-nomad-300">No songs found.</p>
    </Container>
</template>

<script setup>
import { ref } from 'vue';
import { Link, router, usePage } from '@inertiajs/vue3';
import axios from 'axios';
import Container from '@/Pages/Backstage/Container.vue';

const { songs } = usePage().props;
const fallbackImage = 'https://open.nomadlyrics.com/assets/images/no-artist.png';
const deletingSongId = ref(null);

function retryDownload(id) {
    axios.post('/api/backstage/retry', { id });
}

function deleteSong(id) {
    if (!confirm('Are you sure you want to delete this song? This action cannot be undone.')) {
        return;
    }

    deletingSongId.value = id;
    router.delete(`/backstage/songs/${id}`, {
        onFinish: () => {
            deletingSongId.value = null;
        },
    });
}
</script>
